import * as React from 'react';
import { TileLayer, useMap } from 'react-leaflet';

import { FormControlLabel, Radio } from '@mui/material';
import classNames from 'classnames';
import { useMemo } from "react";
import { MapDataContext } from '../context/map-data';
import { LayerDescription, ProviderShapeItem, mapProvidersShape } from "../data/layers";
import { trackEvent } from '../helpers/tracking';
import styles from '../styles/components/layers-toggle.module.scss';

export const Layers = () => {

  const {
    currentLayer, 
    setCurrentLayer,
    dataSourceModel,
    dataSources,
    setScaleValues,
  } = React.useContext(MapDataContext);

  const layers: LayerDescription[] = useMemo(() => {
    if (dataSourceModel && dataSources && Object.prototype.hasOwnProperty.call(dataSources, dataSourceModel.key)) {
      return dataSources[dataSourceModel.key].layers
    }
    return []
  }, [dataSourceModel, dataSources])

  return (
    <div className={classNames(styles.wrapper)}>
      <FormControlLabel
        key={`none`}
        className={styles.formControl}
        control={
          <Radio 
            value="remember" 
            checked={!currentLayer} 
            onChange={(e: React.SyntheticEvent) => {
              setCurrentLayer(null)
              setScaleValues(null)
              trackEvent("layer-change", {layer: 'none'})
            }}
            color="primary" 
          />}
        label={"Hide all"}
      />
      {layers.map((row: LayerDescription) => {
          const {title, valueKey, hide} = row
          if (!valueKey || hide) return null
          return (
            <FormControlLabel
              key={`${title}`}
              className={styles.formControl}
              // disabled={!valueKey}
              control={
                <Radio 
                  value="remember" 
                  checked={currentLayer?.valueKey === valueKey} 
                  onChange={(e: React.SyntheticEvent) => {
                    setCurrentLayer(row)
                    setScaleValues(null)
                    trackEvent("layer-change", {layer: title})
                  }}
                  color="primary" 
                />}
              label={title}
            />
          )
        })
      }
    </div>
  )
};

interface CurrentMapProps {
  mapProvider?: string
  updateLocationOnMapChange?: boolean
}

export const CurrentMap = ({
  mapProvider,
  updateLocationOnMapChange = true,
}:CurrentMapProps) => {
  const {
    currentMapProvider,
    setZoom,
  } = React.useContext(MapDataContext)

  const map = useMap();

  React.useEffect(() => {
    if (map) {
      map.on('zoomend', () => {
        if (!updateLocationOnMapChange) return
        const zoomLevel = map.getZoom()
        setZoom(zoomLevel)
      });
    }    
    return () => {
      if (map) {
        map.off("zoomend")
      }
    }
  }, [map, updateLocationOnMapChange])
  


  const useProvider: ProviderShapeItem | null = useMemo(() => {
    if (mapProvider && Object.prototype.hasOwnProperty.call(mapProvidersShape, mapProvider)) {
      return mapProvidersShape[mapProvider]
    } else if (currentMapProvider !== null && Object.prototype.hasOwnProperty.call(mapProvidersShape, currentMapProvider)) {
      return mapProvidersShape[currentMapProvider]
    }
    return null
  }, [currentMapProvider, mapProvider])

  if (useProvider === null) return null

  return (
    <TileLayer attribution={useProvider.attribution} url={useProvider.url} />
  )
}

export const Providers = () => {
  const {
    currentMapProvider,
    setCurrentMapProvider
} = React.useContext(MapDataContext)

  const labels = useMemo(() => {
    return Object.keys(mapProvidersShape)
  }, [])

  return (
    <div className={classNames(styles.wrapper)}>
      {labels.map((label: string) => {
        const shapeRow = mapProvidersShape[label]
        const {title, name} = shapeRow
        return (
          <FormControlLabel
            key={`${label}-${title}`}
            className={styles.formControl}
            disabled={!name}
            control={
              <Radio 
                value="remember" 
                checked={currentMapProvider === name} 
                onChange={(e: React.SyntheticEvent) => {
                  setCurrentMapProvider(name)
                  trackEvent("map-provider-change", {map: name})
                }}
                color="primary" 
              />}
            label={title}
          />
        )
      })
    }
    </div>
  )
};

interface LayersToggleProps {
  type?: undefined | 'layers' | 'providers'
}

export const LayersToggle = ({
  type
}: LayersToggleProps) => {

  if (typeof window === "undefined") {
    return null
  }

  if (type === 'layers') {
    return <Layers />
  }
  
  if (type === 'providers') {
    return <Providers />
  }

  return null
};